import TitrePage from "../components/charte/TitrePage";
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, MenuItem, Select, Typography,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import DeckGL from '@deck.gl/react';
import { ScatterplotLayer, BitmapLayer } from '@deck.gl/layers';
import { TileLayer } from '@deck.gl/geo-layers';
import { urlsTuilesDeckGl } from '../utilities/basemap';
import { RAMPE_DENSITE } from '../theme/tribeTheme';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import locationApi from '../utilities/locationApi';
import internalApi from '../utilities/internalApi';
import { AIDE_ANALYSES } from '../aide/textesAide';

const TRIBE_CATEGORIES = [
  'Commerce informel',
  'Restauration',
  'Services financiers',
  'Santé',
  'Éducation',
  'Services de proximité',
  'Automobile',
  'Hôtellerie & logement',
  'Culture & loisirs',
  'Infrastructure & espace public',
];

const TRIBE_COLOR  = '#1F1E1B';
const GOOGLE_COLOR = '#E24B4A';
const OSM_COLOR    = '#1D5FA4';   // référentiel : bleu de la charte

const TRIBE_RGB:  [number, number, number] = [142, 71, 230];
const GOOGLE_RGB: [number, number, number] = [226, 75, 74];
const OSM_RGB:    [number, number, number] = [29, 95, 164];

/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const D = {
  bg:          'var(--c-bg)',
  surface:     'var(--c-surface)',
  border:      'var(--c-border)',
  text:        '#1F1E1B',
  textSub:     '#57534A',
  textMuted:   '#57534A',
  accent:      '#1F1E1B',
  fontDisplay: "var(--font-display)",
  fontBody:    "var(--font-body)",
} as const;

interface Zone { label: string; minLat: number; maxLat: number; minLng: number; maxLng: number; }

const ZONES: Zone[] = [
  { label: 'Sacré Cœur', minLat: 14.70945, maxLat: 14.71406, minLng: -17.47166, maxLng: -17.46560 },
  { label: 'HLM',        minLat: 14.68,    maxLat: 14.72,    minLng: -17.46,    maxLng: -17.42    },
  { label: 'Sandaga',    minLat: 14.66,    maxLat: 14.70,    minLng: -17.44,    maxLng: -17.40    },
  { label: 'Pikine',     minLat: 14.73,    maxLat: 14.78,    minLng: -17.40,    maxLng: -17.35    },
];

interface LocationItem {
  id?: string;
  name?: string;
  locationName?: string;
  latitude?: number;
  longitude?: number;
  createdBy?: string;
  categoryMain?: string;
  locationType?: string;
}

interface MappedPlace { name: string; category: string; lat: number; lng: number; types?: string[]; }

const TRIBE_CATEGORIES_SET = new Set(TRIBE_CATEGORIES);

function mapGoogleCategory(typeOrCat: string): string {
  if (TRIBE_CATEGORIES_SET.has(typeOrCat)) return typeOrCat;
  const t = (typeOrCat || '').toLowerCase();
  if (/restaurant|cafe|food/.test(t))                                    return 'Restauration';
  if (/bank|finance|atm|money_transfer/.test(t))                         return 'Services financiers';
  if (/pharmacy|hospital|clinic|doctor|dentist/.test(t))                 return 'Santé';
  if (/school|university|library/.test(t))                               return 'Éducation';
  if (/lodging|hotel|motel/.test(t))                                     return 'Hôtellerie & logement';
  if (/store|shop|supermarket|convenience_store|clothing_store/.test(t)) return 'Commerce informel';
  if (/car_repair|car_wash|gas_station/.test(t))                         return 'Automobile';
  if (/beauty_salon|hair_care|laundry/.test(t))                          return 'Services de proximité';
  if (/museum|park|stadium|movie_theater/.test(t))                       return 'Culture & loisirs';
  return 'Infrastructure & espace public';
}

function zoneCenter(z: Zone): [number, number] {
  return [(z.minLat + z.maxLat) / 2, (z.minLng + z.maxLng) / 2];
}

function getCategory(item: LocationItem): string {
  return item.categoryMain || 'Autre';
}

function KpiCard({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <Box sx={{
      flex: 1, minWidth: 140,
      background: D.surface,
      border: `1px solid ${D.border}`,
      borderRadius: '8px',
      p: '16px 20px',
    }}>
      <Typography sx={{ fontSize: '12px', color: D.textSub, fontFamily: D.fontBody, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '28px', fontWeight: 700, fontFamily: D.fontDisplay, color: accent || D.text, lineHeight: 1 }}>
        {value}
      </Typography>
    </Box>
  );
}

function DarkTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ background: 'var(--c-surface)', border: '1px solid rgba(31,30,27,0.1)', borderRadius: '8px', p: '10px 14px' }}>
      <Typography sx={{ fontSize: '11px', color: '#57534A', mb: '4px' }}>{label}</Typography>
      {payload.map((p: any) => (
        <Typography key={p.name} sx={{ fontSize: '13px', color: p.color, fontWeight: 600 }}>
          {p.name}: {p.value}%
        </Typography>
      ))}
    </Box>
  );
}

export default function Dataviz() {
  const [zoneIdx, setZoneIdx] = useState(0);
  const zone = ZONES[zoneIdx];

  const [allRawLocations, setAllRawLocations] = useState<LocationItem[]>([]);
  const [google, setGoogle]       = useState<MappedPlace[]>([]);
  const [loadingLoc, setLoadingLoc]       = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const loading = loadingLoc || loadingGoogle;
  const [error, setError]         = useState<string | null>(null);

  const [showTribe,   setShowTribe]   = useState(true);
  const [showGoogle,  setShowGoogle]  = useState(true);
  const [showOsm,     setShowOsm]     = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [catFilter,   setCatFilter]   = useState<string | null>(null);

  const [viewState, setViewState] = useState(() => {
    const [lat, lng] = zoneCenter(ZONES[0]);
    return { longitude: lng, latitude: lat, zoom: 15, pitch: 0, bearing: 0 };
  });

  /* Sync map position when zone changes */
  useEffect(() => {
    const [lat, lng] = zoneCenter(zone);
    setViewState(vs => ({ ...vs, longitude: lng, latitude: lat, zoom: 15 }));
  }, [zoneIdx]);

  /* Derived tribe / osm — client-side filter of allRawLocations by selected zone bbox */
  const { tribe, osm } = useMemo(() => {
    const inZone = allRawLocations.filter(d =>
      d.latitude != null && d.longitude != null &&
      d.latitude  >= zone.minLat && d.latitude  <= zone.maxLat &&
      d.longitude >= zone.minLng && d.longitude <= zone.maxLng
    );
    return {
      tribe: inZone.filter(d => d.createdBy !== 'osm_import'),
      osm:   inZone.filter(d => d.createdBy === 'osm_import'),
    };
  }, [allRawLocations, zone]);

  /* Fetch all locations once on mount */
  useEffect(() => {
    setLoadingLoc(true);
    setError(null);
    // Positions légères, POI supprimés exclus (au lieu de la collection complète).
    locationApi.get('/location/positions')
      .then(res => {
        const data: LocationItem[] = Array.isArray(res.data) ? res.data : (res.data?.content ?? []);
        setAllRawLocations(data);
      })
      .catch(e => setError(String(e?.message ?? 'Erreur de chargement')))
      .finally(() => setLoadingLoc(false));
  }, []);

  /* Fetch Google nearby per zone */
  useEffect(() => {
    setLoadingGoogle(true);
    const [centerLat, centerLng] = zoneCenter(zone);
    internalApi.get('/google/nearby', {
      params: { lat: centerLat, lng: centerLng, radius: 400 },
    }).then(res => {
      const places: MappedPlace[] = (Array.isArray(res.data) ? res.data : [])
        .map((p: any) => ({
          ...p,
          types: p.types ?? (p.type ? [p.type] : []),
          category: mapGoogleCategory(p.category ?? p.type ?? ''),
        }));
      setGoogle(places);
    })
      .catch(e => setError(String(e?.message ?? 'Erreur de chargement')))
      .finally(() => setLoadingGoogle(false));
  }, [zoneIdx]);

  const filteredTribe  = catFilter === null ? tribe  : tribe.filter(t => t.categoryMain === catFilter);
  const filteredOsm    = catFilter === null ? osm    : osm.filter(t => t.categoryMain === catFilter);
  // Le serveur renvoie déjà la catégorie Tribe de chaque lieu Google ; il ne
  // renvoie pas « types » (Z1-18).
  const filteredGoogle = catFilter === null ? google : google.filter(p => p.category === catFilter);

  const tribeTotal  = tribe.length  || 1;
  const googleTotal = google.length || 1;
  const osmTotal    = osm.length    || 1;

  const chartCategories = catFilter === null
    ? TRIBE_CATEGORIES.filter(cat =>
        tribe.filter(t => getCategory(t) === cat).length > 0 ||
        google.filter(p => p.category === cat).length > 0 ||
        osm.filter(t => getCategory(t) === cat).length > 0
      )
    : [catFilter];

  const chartData = chartCategories.map(cat => ({
    category: cat,
    Tribe:  +(tribe.filter(t => getCategory(t) === cat).length  / tribeTotal  * 100).toFixed(1),
    Google: +(google.filter(p => p.category === cat).length     / googleTotal * 100).toFixed(1),
    OSM:    +(osm.filter(t => getCategory(t) === cat).length    / osmTotal    * 100).toFixed(1),
  }));

  const ratio = google.length > 0 ? (tribe.length / google.length).toFixed(1) : '—';

  /* ── Deck.gl layers ── */
  const heatmapData = useMemo(() => {
    const pts: Array<{ lng: number; lat: number }> = [];
    if (showTribe)  filteredTribe.filter(t => t.latitude && t.longitude).forEach(t => pts.push({ lng: t.longitude!, lat: t.latitude! }));
    if (showGoogle) filteredGoogle.forEach(p => pts.push({ lng: p.lng, lat: p.lat }));
    if (showOsm)    filteredOsm.filter(t => t.latitude && t.longitude).forEach(t => pts.push({ lng: t.longitude!, lat: t.latitude! }));
    return pts;
  }, [filteredTribe, filteredGoogle, filteredOsm, showTribe, showGoogle, showOsm]);

  const layers = useMemo(() => [
    new TileLayer({
      id: 'base-tiles',
      data: urlsTuilesDeckGl(),
      tileSize: 256,
      renderSubLayers: (props: any) => {
        const { west, south, east, north } = props.tile.bbox;
        return new BitmapLayer(props, {
          data: undefined,
          image: props.data,
          bounds: [west, south, east, north],
        });
      },
    }),
    new ScatterplotLayer<LocationItem>({
      id: 'scatter-tribe',
      data: filteredTribe.filter(t => t.latitude && t.longitude),
      visible: showTribe,
      getPosition: (d) => [d.longitude!, d.latitude!],
      getFillColor: TRIBE_RGB,
      getRadius: 6,
      radiusUnits: 'pixels',
      radiusMinPixels: 6,
      pickable: true,
      opacity: 0.8,
    }),
    new ScatterplotLayer<MappedPlace>({
      id: 'scatter-google',
      data: filteredGoogle,
      visible: showGoogle,
      getPosition: (d) => [d.lng, d.lat],
      getFillColor: GOOGLE_RGB,
      getRadius: 6,
      radiusUnits: 'pixels',
      radiusMinPixels: 6,
      pickable: true,
      opacity: 0.8,
    }),
    new ScatterplotLayer<LocationItem>({
      id: 'scatter-osm',
      data: filteredOsm.filter(t => t.latitude && t.longitude),
      visible: showOsm,
      getPosition: (d) => [d.longitude!, d.latitude!],
      getFillColor: OSM_RGB,
      getRadius: 6,
      radiusUnits: 'pixels',
      radiusMinPixels: 6,
      pickable: true,
      opacity: 0.8,
    }),
    new HeatmapLayer({
      id: 'heatmap',
      data: heatmapData,
      visible: showHeatmap,
      getPosition: (d: any) => [d.lng, d.lat],
      getWeight: 1,
      radiusPixels: 60,
      intensity: 3,
      threshold: 0.05,
      colorRange: RAMPE_DENSITE as any,
    }),
  ], [filteredTribe, filteredGoogle, filteredOsm, heatmapData, showTribe, showGoogle, showOsm, showHeatmap]);

  return (
    <Box sx={{ p: 3, background: D.bg, minHeight: '100vh', color: D.text }}>
      {/* ── Header + zone selector ── */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <TitrePage domaine="analyses" aide={AIDE_ANALYSES.comparaison}>Comparaison des sources</TitrePage>
          <Typography sx={{ fontSize: '13px', color: D.textSub, fontFamily: D.fontBody }}>
            Tribe face à Google Maps et OpenStreetMap, zone par zone
          </Typography>
        </Box>
        <Select
          value={zoneIdx}
          onChange={e => setZoneIdx(Number(e.target.value))}
          size="small"
          sx={{
            background: D.surface,
            color: D.text,
            border: `1px solid ${D.border}`,
            borderRadius: '8px',
            fontFamily: D.fontBody,
            fontSize: '14px',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSvgIcon-root': { color: D.textSub },
          }}
        >
          {ZONES.map((z, i) => (
            <MenuItem key={z.label} value={i}>{z.label}</MenuItem>
          ))}
        </Select>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── KPI cards ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <KpiCard label="Total Tribe"          value={loading ? '…' : String(tribe.length)}  accent={TRIBE_COLOR}  />
        <KpiCard label="Total Google"         value={loading ? '…' : String(google.length)} accent={GOOGLE_COLOR} />
        <KpiCard label="Total référentiel"            value={loading ? '…' : String(osm.length)}    accent={OSM_COLOR}    />
        <KpiCard label="Ratio Tribe / Google" value={loading ? '…' : ratio} />
      </Box>

      {/* ── Category filter ── */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '13px', color: D.textSub, fontFamily: D.fontBody, fontWeight: 600 }}>
          Catégorie :
        </Typography>
        <Select
          value={catFilter ?? ''}
          onChange={e => setCatFilter(e.target.value === '' ? null : e.target.value)}
          size="small"
          sx={{
            background: D.surface, color: D.text,
            border: `1px solid ${D.border}`, borderRadius: '8px',
            fontFamily: D.fontBody, fontSize: '13px', minWidth: 220,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSvgIcon-root': { color: D.textSub },
          }}
        >
          <MenuItem value="">Toutes</MenuItem>
          {TRIBE_CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
      </Box>

      {/* ── Map ── */}
      <Box sx={{ mb: 3, background: D.surface, border: `1px solid ${D.border}`, borderRadius: '8px', overflow: 'hidden' }}>
        {/* Layer controls */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${D.border}` }}>
          <Typography sx={{ fontFamily: D.fontBody, fontSize: '13px', fontWeight: 600, color: D.textSub, mr: 1 }}>
            Couches :
          </Typography>
          {([
            { label: 'Tribe',   color: TRIBE_COLOR,  show: showTribe,   toggle: () => setShowTribe(p => !p)   },
            { label: 'Google',  color: GOOGLE_COLOR, show: showGoogle,  toggle: () => setShowGoogle(p => !p)  },
            { label: 'Référentiel', color: OSM_COLOR,    show: showOsm,     toggle: () => setShowOsm(p => !p)     },
            { label: 'Heatmap', color: '#7C5600',    show: showHeatmap, toggle: () => setShowHeatmap(p => !p) },
          ] as const).map(({ label, color, show, toggle }) => (
            <Chip
              key={label}
              label={label}
              size="small"
              onClick={toggle as any}
              sx={{
                fontFamily: D.fontBody,
                backgroundColor: show ? `${color}22` : 'transparent',
                color: show ? color : D.textMuted,
                border: `1px solid ${show ? color : D.border}`,
                cursor: 'pointer',
              }}
            />
          ))}
        </Box>

        {/* DeckGL map */}
        <Box sx={{ position: 'relative', height: 400, background: D.bg }}>
          <DeckGL
            viewState={viewState}
            onViewStateChange={({ viewState: vs }: any) => setViewState(vs)}
            controller={true}
            layers={layers}
            style={{ width: '100%', height: '100%' }}
            getTooltip={({ object, layer }: any) => {
              if (!object) return null;
              const sourceMap: Record<string, string> = {
                'scatter-tribe':  'Tribe',
                'scatter-google': 'Google',
                'scatter-osm':    'Référentiel',
              };
              const source   = (layer && sourceMap[layer.id]) ?? 'Tribe';
              const name     = object.name ?? object.locationName ?? '—';
              const category = object.category ?? object.categoryMain ?? '—';
              return {
                html: `<div><strong>${name}</strong><br/>${category}<br/><span style="opacity:0.65">${source}</span></div>`,
                style: { backgroundColor: 'var(--c-surface)', color: '#1F1E1B', border: '1.5px solid #1F1E1B', padding: '8px', borderRadius: '4px', fontSize: '13px' },
              };
            }}
          />
        </Box>
      </Box>

      {/* ── Category chart ── */}
      <Box sx={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: '8px', p: 2 }}>
        <Typography sx={{ fontFamily: D.fontBody, fontSize: '14px', fontWeight: 600, color: D.textSub, mb: 2 }}>
          Répartition par catégorie — Tribe&nbsp;: {tribe.length} pts | Google&nbsp;: {google.length} pts | Référentiel&nbsp;: {osm.length} pts
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: D.accent }} />
          </Box>
        ) : chartData.length === 0 ? (
          <Typography sx={{ color: D.textMuted, fontFamily: D.fontBody, fontSize: '13px', textAlign: 'center', py: 3 }}>
            Aucune donnée
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, chartCategories.length * 44)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(31,30,27,0.05)" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} tick={{ fill: 'rgba(31,30,27,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="category" width={170}
                tick={{ fill: 'rgba(31,30,27,0.6)', fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip content={<DarkTooltip />} cursor={{ fill: 'rgba(31,30,27,0.03)' }} />
              <Legend wrapperStyle={{ fontFamily: D.fontBody, fontSize: '12px' }} />
              <Bar dataKey="Tribe"  fill={TRIBE_COLOR}  radius={[0, 4, 4, 0]} maxBarSize={14} />
              <Bar dataKey="Google" fill={GOOGLE_COLOR} radius={[0, 4, 4, 0]} maxBarSize={14} />
              <Bar dataKey="OSM" name="Référentiel" fill={OSM_COLOR}    radius={[0, 4, 4, 0]} maxBarSize={14} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
}
